import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const InputGroup = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="input-group"
    role="group"
    className={cn(
      'tw:group/input-group tw:relative tw:flex tw:h-9 tw:w-full tw:min-w-0 tw:items-center tw:rounded-lg tw:border tw:border-input tw:bg-transparent tw:shadow-xs tw:transition-[color,box-shadow] tw:outline-none tw:hover:not-has-[[data-slot=input-group-control]:disabled]:border-ring tw:has-[[data-slot=input-group-control]:focus-visible]:border-ring tw:has-[[data-slot=input-group-control]:focus-visible]:shadow-[0_0_0_3px_color-mix(in_oklab,var(--ring)_50%,transparent)] tw:has-[[data-slot][aria-invalid=true]]:border-destructive tw:has-[[data-slot=input-group-control]:disabled]:cursor-not-allowed tw:has-[[data-slot=input-group-control]:disabled]:bg-muted tw:has-[[data-slot=input-group-control]:disabled]:opacity-80 tw:has-[[data-slot][aria-invalid=true]]:shadow-[0_0_0_3px_color-mix(in_oklab,var(--ring-destructive)_50%,transparent)] tw:has-[>[data-align=block-end]]:h-auto tw:has-[>[data-align=block-end]]:flex-col tw:has-[>[data-align=block-start]]:h-auto tw:has-[>[data-align=block-start]]:flex-col tw:dark:has-[[data-slot][aria-invalid=true]]:border-destructive/50 tw:has-[>[data-align=block-end]]:[&>input]:pt-3 tw:has-[>[data-align=block-start]]:[&>input]:pb-3 tw:has-[>[data-align=inline-end]]:[&>input]:pr-1.5 tw:has-[>[data-align=inline-start]]:[&>input]:pl-1.5',
      className
    )}
    {...props}
  />
));
InputGroup.displayName = 'InputGroup';

const inputGroupAddonVariants = cva(
  'tw:flex tw:h-auto tw:cursor-text tw:items-center tw:justify-center tw:gap-2 tw:py-1.5 tw:text-sm tw:font-medium tw:text-muted-foreground tw:select-none tw:group-data-[disabled=true]/input-group:opacity-50 tw:[&>kbd]:rounded-[calc(var(--radius)-5px)] tw:[&>svg:not([class*=size-])]:size-4',
  {
    variants: {
      align: {
        'inline-start':
          'tw:order-first tw:pl-2 tw:has-[>button]:-ml-1 tw:has-[>kbd]:ml-[-0.15rem]',
        'inline-end':
          'tw:order-last tw:pr-2 tw:has-[>button]:-mr-1 tw:has-[>kbd]:mr-[-0.15rem]',
        'block-start':
          'tw:order-first tw:w-full tw:justify-start tw:px-2.5 tw:pt-2 tw:group-has-[>input]/input-group:pt-2 tw:[.border-b]:pb-2',
        'block-end':
          'tw:order-last tw:w-full tw:justify-start tw:px-2.5 tw:pb-2 tw:group-has-[>input]/input-group:pb-2 tw:[.border-t]:pt-2',
      },
    },
    defaultVariants: {
      align: 'inline-start',
    },
  }
);

function InputGroupAddon({
  className,
  align = 'inline-start',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    // Click-to-focus is a convenience for sighted mouse users; keyboard users
    // reach the input via Tab, so no keyboard handler is needed here.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) {
          return;
        }
        e.currentTarget.parentElement?.querySelector('input')?.focus();
      }}
      {...props}
    />
  );
}

const inputGroupButtonVariants = cva(
  'tw:flex tw:items-center tw:gap-2 tw:text-sm tw:shadow-none',
  {
    variants: {
      size: {
        xs: 'tw:h-6 tw:gap-1 tw:rounded-[calc(var(--radius)-5px)] tw:px-1.5 tw:[&>svg:not([class*=size-])]:size-3.5',
        sm: 'tw:h-8 tw:gap-1 tw:rounded-[min(theme(--radius-md),10px)] tw:px-3 tw:py-1.5 tw:[&>svg:not([class*=size-])]:size-3.5',
        'icon-xs':
          'tw:size-6 tw:rounded-[calc(var(--radius)-5px)] tw:p-0 tw:has-[>svg]:p-0',
        'icon-sm': 'tw:size-8 tw:p-0 tw:has-[>svg]:p-0',
      },
    },
    defaultVariants: {
      size: 'xs',
    },
  }
);

const InputGroupButton = React.forwardRef<
  HTMLButtonElement,
  Omit<React.ComponentProps<typeof Button>, 'size' | 'type'> &
    VariantProps<typeof inputGroupButtonVariants> & {
      type?: 'button' | 'submit' | 'reset';
    }
>(function InputGroupButton(
  { className, type = 'button', variant = 'ghost', size = 'xs', ...props },
  ref
) {
  return (
    <Button
      ref={ref}
      type={type}
      data-size={size}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  );
});

function InputGroupText({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      className={cn(
        'tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-muted-foreground tw:[&_svg]:pointer-events-none tw:[&_svg:not([class*=size-])]:size-4',
        className
      )}
      {...props}
    />
  );
}

const InputGroupInput = React.forwardRef<
  HTMLInputElement,
  React.ComponentPropsWithoutRef<'input'>
>(({ className, ...props }, ref) => {
  return (
    <Input
      ref={ref}
      data-slot="input-group-control"
      className={cn(
        'tw:flex-1 tw:rounded-none tw:border-0 tw:bg-transparent tw:shadow-none tw:ring-0 tw:focus-visible:ring-0 tw:focus-visible:shadow-none tw:aria-invalid:ring-0 tw:dark:bg-transparent',
        className
      )}
      {...props}
    />
  );
});
InputGroupInput.displayName = 'InputGroupInput';

function InputGroupTextarea({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'textarea'>) {
  return (
    <Textarea
      data-slot="input-group-control"
      className={cn(
        'tw:flex-1 tw:rounded-none tw:border-0 tw:bg-transparent tw:py-3 tw:shadow-none tw:ring-0 tw:focus-visible:ring-0 tw:focus-visible:shadow-none tw:aria-invalid:ring-0 tw:dark:bg-transparent',
        className
      )}
      {...props}
    />
  );
}

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
  InputGroupInput,
  InputGroupTextarea,
};
